import { useNavigate } from "react-router-dom";
import { ArrowRight, Users, Briefcase, Building2, Car, Sparkles, ShieldCheck, ShoppingBag } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhySmartBanner from "@/components/WhySmartBanner";

const LANES = [
    {
        id: "book",
        testid: "join-lane-book",
        title: "I need a service",
        copy: "Cleaning, laundry, chef, babysitter, handyman, barber, car wash, transport, moving — book a Royal pro in under 60 seconds.",
        icon: Users,
        cta: "Start booking",
        to: "/auth?mode=register&role=customer&next=/services",
        bg: "from-amber-400/15 via-yellow-500/10 to-transparent",
        ring: "ring-amber-400/40",
    },
    {
        id: "work",
        testid: "join-lane-work",
        title: "I offer a service",
        copy: "Cleaner, braider, nail tech, barber, chef, electrician, plumber, painter, driver, mover — join SMART and get live jobs sent to you.",
        icon: Briefcase,
        cta: "Become a Royal Pro",
        to: "/auth?mode=register&role=provider&next=/become-provider",
        bg: "from-emerald-400/15 via-emerald-500/10 to-transparent",
        ring: "ring-emerald-400/40",
    },
    {
        id: "sell",
        testid: "join-lane-sell",
        title: "I have something to sell",
        copy: "List clothes, furniture, electronics, baby gear, tools, jewelry — new, pre-owned or used. SMART drivers can deliver straight to the buyer.",
        icon: ShoppingBag,
        cta: "Open live marketplace",
        to: "/marketplace",
        bg: "from-fuchsia-400/15 via-pink-500/10 to-transparent",
        ring: "ring-fuchsia-400/40",
    },
    {
        id: "company",
        testid: "join-lane-company",
        title: "Company / Vendor",
        copy: "Hotels, villas, Airbnbs, offices — plug SMART into your guest experience with net-30 billing and partner rates.",
        icon: Building2,
        cta: "Open venue console",
        to: "/auth?mode=register&role=customer&next=/venue/register",
        bg: "from-sky-400/15 via-indigo-400/10 to-transparent",
        ring: "ring-sky-400/40",
    },
];


export default function JoinPage() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-background" data-testid="join-page">
            <Header />
            <main className="max-w-6xl mx-auto px-5 lg:px-8 pt-10 pb-24">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FFD700]/40 text-[10px] uppercase tracking-[0.3em] text-amber-300/90 mb-5">
                        <Sparkles size={12} /> Pick your lane
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground">
                        Welcome to <span className="gold-text">SMART</span>
                    </h1>
                    <p className="mt-5 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                        Three ways in. Tap the one that fits — we handle the rest.
                    </p>
                </div>

                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="join-lanes-grid">
                    {LANES.map((lane) => (
                        <button
                            key={lane.id}
                            data-testid={lane.testid}
                            onClick={() => navigate(lane.to)}
                            className={`group text-left relative rounded-2xl border border-white/10 ${lane.ring} ring-1 bg-gradient-to-br ${lane.bg} p-7 min-h-[270px] flex flex-col justify-between hover:border-[#FFD700]/60 hover:ring-[#FFD700]/60 transition-all duration-500 overflow-hidden`}
                        >
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-black/40 border border-white/10 grid place-items-center mb-5 group-hover:scale-110 group-hover:border-[#FFD700]/60 transition-all">
                                    <lane.icon size={22} className="text-[#FFD700]" />
                                </div>
                                <h2 className="font-serif text-2xl text-foreground mb-3">{lane.title}</h2>
                                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{lane.copy}</p>
                            </div>
                            <div className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.26em] text-[#FFD700] group-hover:gap-3 transition-all">
                                {lane.cta}
                                <ArrowRight size={14} />
                            </div>
                        </button>
                    ))}
                </div>

                {/* Driver Charter sub-lane */}
                <div className="mt-14" data-testid="driver-charter-combo">
                    <div className="rounded-2xl border border-[#FFD700]/30 bg-gradient-to-br from-[#2d1a05] via-background to-background p-7 lg:p-10">
                        <div className="grid lg:grid-cols-[auto,1fr,auto] gap-6 items-center">
                            <div className="w-14 h-14 rounded-xl bg-black/50 border border-[#FFD700]/40 grid place-items-center">
                                <Car size={26} className="text-[#FFD700]" />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mb-2">Have your own vehicle?</div>
                                <h3 className="font-serif text-2xl lg:text-3xl text-foreground">
                                    Drive <span className="gold-text">+</span> Charter Combo
                                </h3>
                                <p className="mt-3 text-xs sm:text-sm text-muted-foreground max-w-2xl">
                                    List your car on SMART Wheels and work as a Royal Driver in one application —
                                    earn as both a chauffeur and a vehicle owner. Shared insurance checks, one Royal background approval, double income.
                                </p>
                                <div className="mt-4 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.24em]">
                                    <span className="px-2.5 py-1 rounded-full border border-white/10 text-muted-foreground">Daily charters</span>
                                    <span className="px-2.5 py-1 rounded-full border border-white/10 text-muted-foreground">Airport runs</span>
                                    <span className="px-2.5 py-1 rounded-full border border-white/10 text-muted-foreground">Rental marketplace</span>
                                    <span className="px-2.5 py-1 rounded-full border border-white/10 text-muted-foreground">Weekly payout</span>
                                </div>
                            </div>
                            <div className="flex flex-col gap-3">
                                <button
                                    data-testid="driver-charter-apply-btn"
                                    onClick={() => navigate("/auth?mode=register&role=provider&next=/wheels/apply")}
                                    className="px-5 py-3 rounded-full bg-[#FFD700] text-black text-[11px] uppercase tracking-[0.24em] font-semibold hover:bg-amber-300 transition-all inline-flex items-center gap-2"
                                >
                                    Apply as Driver <ArrowRight size={14} />
                                </button>
                                <button
                                    data-testid="driver-charter-list-btn"
                                    onClick={() => navigate("/auth?mode=register&role=provider&next=/vehicles/list")}
                                    className="px-5 py-3 rounded-full border border-[#FFD700]/50 text-[#FFD700] text-[11px] uppercase tracking-[0.24em] hover:bg-[#FFD700]/10 transition-all inline-flex items-center gap-2"
                                >
                                    List your vehicle <ArrowRight size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-16 flex flex-wrap justify-center gap-3 text-[10px] uppercase tracking-[0.26em] text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-[#FFD700]" /> Royal Protection</span>
                    <span>·</span>
                    <span>Jamaica-wide</span>
                    <span>·</span>
                    <span>24/7 Response</span>
                </div>
            </main>
            <WhySmartBanner />
            <Footer />
        </div>
    );
}
